import { Combobox, Datepicker, Input, Mask, Switch } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ConversationAttributeItem } from '@/entities/attributes/model/api/types';
import type { AttributeValueKind } from '../model/attributeValues';

/** Шаблон ввода длительности `чч:мм:сс`: на месте `_` — цифра */
const DURATION_MASK = '__:__:__';

/** Что допускается на месте `_` в шаблоне длительности */
const DURATION_MASK_REPLACEMENT = { _: /\d/ };

type Props = {
  attribute: ConversationAttributeItem;
  /** Вид поля — по `getAttributeValueKind` */
  kind: AttributeValueKind;
  /** Имя поля значения в форме потребителя */
  name: string;
  disabled?: boolean;
};

/**
 * Поле ввода одного значения атрибута в форме потребителя. Компонент выбирается по виду поля;
 * название атрибута поле не показывает — его выводит строка потребителя.
 */
export const AttributeValueField: FC<Props> = ({ attribute, kind, name, disabled }) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();
  // Подпись переключателя «да/нет» повторяет его значение
  const value = useWatch({ control, name });

  // Удалённое значение справочника новым значением атрибута не выбирается
  const selectOptions = useMemo(
    () =>
      (attribute.selectValues ?? [])
        .filter((selectValue) => !selectValue.isDeleted)
        .map((selectValue) => ({ value: selectValue.code, label: selectValue.value })),
    [attribute.selectValues],
  );

  switch (kind) {
    case 'multiselect':
      return (
        <Combobox
          control={control}
          name={name}
          isMulti
          options={selectOptions}
          placeholder={t('attributeFilters.valuesPlaceholder')}
          disabled={disabled}
          className="w-full"
        />
      );
    case 'select':
      return (
        <Combobox
          control={control}
          name={name}
          options={selectOptions}
          placeholder={t('attributeFilters.selectValuePlaceholder')}
          disabled={disabled}
          className="w-full"
        />
      );
    case 'integer':
    case 'decimal':
      return (
        <Input
          control={control}
          name={name}
          type="number"
          placeholder={t('attributeFilters.valuePlaceholder')}
          disabled={disabled}
          className="w-full"
        />
      );
    case 'duration':
      return (
        <Mask
          control={control}
          name={name}
          mask={DURATION_MASK}
          replacement={DURATION_MASK_REPLACEMENT}
          placeholder={t('attributeFilters.durationPlaceholder')}
          disabled={disabled}
        />
      );
    case 'boolean':
      return <Switch control={control} name={name} label={value ? t('yes') : t('no')} disabled={disabled} />;
    case 'date':
      return <Datepicker control={control} name={name} outputType="iso-date" disabled={disabled} />;
    case 'timestamp':
      return <Datepicker control={control} name={name} showTimeInputs disabled={disabled} />;
    case 'text':
      return (
        <Input
          control={control}
          name={name}
          placeholder={t('attributeFilters.valuePlaceholder')}
          disabled={disabled}
          className="w-full"
        />
      );
  }
};
